import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Stat } from '../components/Stat';
import { Skel } from '../components/Skel';
import { Banner } from '../components/Banner';
import { Crumbs } from '../components/Crumbs';
import { EmptyState } from '../components/EmptyState';
import type { PersonCard } from '../data/types';

interface PeopleProps {
  slug: string;
}

/**
 * Карточка физлица (директор / учредитель / ИП).
 *
 * Endpoint: GET /v1/persons/{slug}
 *
 * NB: На 2026-05 таблица persons заполнена частично — большинство physlits
 * возвращают 404. UI показывает graceful EmptyState с пояснением что данные
 * ждут наполнения через ETL ЕГРЮЛ paid API. Когда это произойдёт — все
 * существующие компоненты заработают без изменений.
 *
 * ИНН ФЛ маскируется для не-auth пользователей (152-ФЗ).
 */
export function People({ slug }: PeopleProps) {
  const [person, setPerson] = useState<PersonCard | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);

  useEffect(() => {
    setPerson(null);
    setError(null);
    const ac = new AbortController();
    api
      .person(slug, { signal: ac.signal })
      .then(setPerson)
      .catch((e) => {
        if (e.name !== 'AbortError') {
          if (e instanceof ApiError) {
            setError({ status: e.status, message: e.message });
          } else {
            setError({ status: 0, message: String(e) });
          }
        }
      });
    return () => ac.abort();
  }, [slug]);

  // 404 — graceful EmptyState, не error banner
  if (error && error.status === 404) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Поиск', href: '#/search' }, { label: slug }]} />
        <EmptyState
          icon="👤"
          title="Карточка физлица не найдена"
          body={
            <>
              Данные о физических лицах (директорах и учредителях) загружаются
              постепенно через ЕГРЮЛ. Если вы ищете конкретного человека —
              откройте карточку компании, в которой он работает, и оттуда
              перейдите по ссылке на руководителя или учредителя.
            </>
          }
        />
      </main>
    );
  }

  if (error) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Поиск', href: '#/search' }, { label: slug }]} />
        <Banner tone="risk" title="Не удалось загрузить карточку">
          API {error.status}: {error.message}
        </Banner>
      </main>
    );
  }

  if (!person) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Поиск', href: '#/search' }, { label: slug }]} />
        <Skel height={32} width="60%" style={{ marginTop: 16 }} />
        <Skel height={20} width="40%" style={{ marginTop: 8 }} />
        <Skel count={3} height={64} radius={12} style={{ marginTop: 24 }} />
      </main>
    );
  }

  const flagBadges = collectFlagBadges(person.risk_flags);
  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs
        items={[
          { label: 'Главная', href: '#/' },
          { label: 'Поиск', href: '#/search' },
          { label: person.full_name },
        ]}
      />

      <header style={{ marginTop: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={typePill}>Физлицо</span>
          {person.gender && <span style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>{person.gender}</span>}
        </div>
        <h1
          style={{
            fontSize: 'clamp(22px, 3vw, 32px)',
            margin: 0,
            color: 'var(--navy-700, #0b1736)',
            overflowWrap: 'anywhere',
          }}
        >
          {person.full_name}
        </h1>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 12,
            fontSize: 13,
          }}
        >
          {person.inn && <code style={pillStyle}>ИНН {person.inn}</code>}
          {person.birth_year && <span style={metaText}>{person.birth_year} г.р.</span>}
          {person.region_code && <span style={metaText}>Регион {person.region_code}</span>}
          {flagBadges.map((f) => (
            <span key={f.label} style={f.warn ? alertPill : badgePill}>
              {f.icon} {f.label}
            </span>
          ))}
        </div>
      </header>

      {/* Stats: участие в компаниях */}
      <section
        aria-label="Участие в компаниях"
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          marginBottom: 24,
        }}
      >
        <Stat label="Всего предприятий" value={person.totals.all_entities} />
        <Stat label="Как директор" value={person.totals.as_director} />
        <Stat label="Как учредитель" value={person.totals.as_founder} />
        <Stat label="Как ИП" value={person.totals.ip} />
        <Stat label="Активных" value={person.totals.active} />
      </section>

      {/* Risk flags */}
      {flagBadges.some((f) => f.warn) && (
        <Banner tone="warn" title="Рисковые флаги">
          У этого физлица есть один или несколько негативных сигналов:{' '}
          {flagBadges
            .filter((f) => f.warn)
            .map((f) => f.label)
            .join(', ')}
          . Рекомендуем дополнительную проверку при заключении договоров.
        </Banner>
      )}

      {/* Сводка по доходам */}
      {person.total_revenue_last_year && (
        <div style={cardBlock}>
          <h2 style={blockTitle}>Финансовая активность</h2>
          <Row
            label="Совокупная выручка предприятий"
            value={`${fmtRevenue(person.total_revenue_last_year)} (за прошлый год)`}
          />
        </div>
      )}

      {/* Disclaimer */}
      <p
        style={{
          marginTop: 32,
          fontSize: 12,
          color: 'var(--muted, #6b7287)',
          textAlign: 'center',
        }}
      >
        Данные ЕГРЮЛ + ФНС. ИНН ФЛ маскируется согласно 152-ФЗ для незарегистрированных пользователей.
      </p>
    </main>
  );
}

interface FlagBadge {
  label: string;
  icon: string;
  warn: boolean;
}

function collectFlagBadges(flags: PersonCard['risk_flags']): FlagBadge[] {
  const badges: FlagBadge[] = [];
  if (flags.disqualified) badges.push({ label: 'Дисквалифицирован', icon: '⚠', warn: true });
  if (flags.mass_director) badges.push({ label: 'Массовый директор', icon: '⚠', warn: true });
  if (flags.mass_founder) badges.push({ label: 'Массовый учредитель', icon: '⚠', warn: true });
  if (flags.sanctioned) badges.push({ label: 'Под санкциями', icon: '⚠', warn: true });
  if (flags.extremist) badges.push({ label: 'Экстремист', icon: '⚠', warn: true });
  if (flags.foreign_agent) badges.push({ label: 'Иноагент', icon: '⚠', warn: true });
  if (flags.bankrupt_link) badges.push({ label: 'Связь с банкротством', icon: '⚠', warn: true });
  return badges;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        padding: '6px 0',
        borderBottom: '1px solid var(--border, #e6e2d6)',
        fontSize: 14,
      }}
    >
      <div style={{ color: 'var(--muted, #6b7287)', minWidth: 140, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

const typePill: React.CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 4,
  background: 'var(--bg, #fbfaf7)',
  color: 'var(--muted, #6b7287)',
  border: '1px solid var(--border, #e6e2d6)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontWeight: 600,
};
const pillStyle: React.CSSProperties = {
  background: 'var(--surface-quiet, #f5f3ec)',
  padding: '4px 8px',
  borderRadius: 4,
  fontFamily: 'JetBrains Mono, monospace',
};
const metaText: React.CSSProperties = {
  color: 'var(--muted, #6b7287)',
};
const badgePill: React.CSSProperties = {
  background: 'var(--bg, #fbfaf7)',
  color: 'var(--navy-500, #1a2d5e)',
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: 12,
  border: '1px solid var(--border, #e6e2d6)',
};
const alertPill: React.CSSProperties = {
  background: '#fde2e2',
  color: '#a31919',
  padding: '3px 10px',
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 12,
  border: '1px solid #e89191',
};
const cardBlock: React.CSSProperties = {
  background: 'var(--surface, #ffffff)',
  border: '1px solid var(--border, #e6e2d6)',
  borderRadius: 12,
  padding: 20,
  marginTop: 24,
};
const blockTitle: React.CSSProperties = {
  fontFamily: 'Fraunces, Georgia, serif',
  fontSize: 18,
  margin: '0 0 12px',
  color: 'var(--navy-700, #0b1736)',
};

function fmtRevenue(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  if (Math.abs(n) >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(1)} трлн ₽`;
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} млрд ₽`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн ₽`;
  return `${n.toLocaleString('ru-RU')} ₽`;
}
